/**
 * A Solution page over the runs of pipelines published to it (ADR-0032).
 *
 * Reusable: eTMF intake today, Release validation and others later — the page
 * passes its `solutionPage` id and its copy, nothing else changes. Results
 * only, for every role: KPIs for a period, a documents-per-day trend, the runs
 * (filter by pipeline and status) and, per run, what happened to each
 * document. Never the canvas, the configuration or the run log.
 *
 * When nothing is published yet the page is still a working page: it says how
 * a Developer publishes a pipeline here, and links Developer+ to Projects.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconLayoutDashboard } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { useEffectiveRoles } from "@/features/auth/RolePreviewContext";
import { formatUtc } from "@/features/solutions/shared/runMeta";
import { useMe } from "@/features/users/useMe";
import {
  getResultsSummary,
  listPublishedPipelines,
  listResultsRuns,
  type PublishedPipelineOut,
  type ResultsRunOut,
  type ResultsSummaryOut,
} from "@/shared/api/solutionPages";
import { lifecycleColor, statusColor } from "@/shared/ui/palette";
import { RunResultDetail } from "./RunResultDetail";
import { TrendChart } from "./TrendChart";
import {
  PERIOD_OPTIONS,
  STATUS_FILTER_OPTIONS,
  TRIGGER_LABEL,
  formatCount,
  formatDuration,
  formatPercent,
  runResult,
} from "./resultsMeta";

export interface PipelineResultsDashboardProps {
  /** The page id pipelines publish to, e.g. `etmf_intake`. */
  solutionPage: string;
  /** The page's own name, as the sidebar says it. */
  title: string;
  description?: string;
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Paper withBorder radius="md" p="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fz={28} fw={700} lh={1.2}>
        {value}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </Paper>
  );
}

function EmptyState({ title, developer }: { title: string; developer: boolean }) {
  return (
    <Paper withBorder radius="md" p="xl">
      <Stack gap="sm" align="flex-start">
        <IconLayoutDashboard size={28} />
        <Title order={4}>No pipeline shows its results here yet</Title>
        <Text size="sm" c="dimmed" maw={640}>
          This page shows the runs of the pipelines published to it — which documents went to Veeva,
          which failed and why. A Developer publishes a pipeline from its canvas:{" "}
          <b>⋯ → Show results in… → {title}</b>. Its runs then appear here for everyone.
        </Text>
        {developer && (
          <Button component={RouterLink} to="/projects" variant="light" size="sm">
            Open Projects
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

export function PipelineResultsDashboard({
  solutionPage,
  title,
  description,
}: PipelineResultsDashboardProps) {
  const getToken = useAccessToken();
  const { data: me } = useMe();
  const roles = useEffectiveRoles(me?.roles ?? []);
  const developer = roles.includes("developer") || roles.includes("administrator");

  const [pipelines, setPipelines] = useState<PublishedPipelineOut[] | null>(null);
  const [days, setDays] = useState("30");
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [summary, setSummary] = useState<ResultsSummaryOut | null>(null);
  const [runs, setRuns] = useState<ResultsRunOut[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // Picking a run slides the page down to its details, which sit below the
  // runs table and are otherwise easy to miss.
  // Scrolled once the details have loaded (before that the box is only a
  // spinner), to the top of the screen below the header bar.
  const detailRef = useRef<HTMLDivElement | null>(null);
  const scrollToDetail = useCallback(() => {
    requestAnimationFrame(() =>
      detailRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }),
    );
  }, []);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await listPublishedPipelines(await getToken(), solutionPage);
        if (!cancelled) setPipelines(res.items);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load the page.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, solutionPage]);

  const load = useCallback(async () => {
    const token = await getToken();
    const filter = { days: Number(days), pipelineId, status };
    const [s, r] = await Promise.all([
      getResultsSummary(token, solutionPage, filter),
      listResultsRuns(token, solutionPage, filter),
    ]);
    return { s, r: r.items };
  }, [getToken, solutionPage, days, pipelineId, status]);

  useEffect(() => {
    if (!pipelines || pipelines.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const { s, r } = await load();
        if (cancelled) return;
        setSummary(s);
        setRuns(r);
        setSelected((cur) => (cur && r.some((x) => x.run_id === cur) ? cur : null));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load results.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pipelines, load]);

  return (
    <Container fluid py="lg">
      <Stack gap="md">
        <div>
          <Title order={2}>{title}</Title>
          {description && <Text c="dimmed">{description}</Text>}
        </div>

        {error && (
          <Alert color="red" title="Could not load the results">
            {error}
          </Alert>
        )}

        {pipelines === null && !error && <Loader size="sm" aria-label="Loading" />}

        {pipelines !== null && pipelines.length === 0 && (
          <EmptyState title={title} developer={developer} />
        )}

        {pipelines !== null && pipelines.length > 0 && (
          <>
            <Group gap="sm" align="flex-end" wrap="wrap">
              <Select
                label="Period"
                data={PERIOD_OPTIONS}
                value={days}
                onChange={(v) => setDays(v ?? "30")}
                allowDeselect={false}
                w={170}
              />
              <Select
                label="Pipeline"
                placeholder="All pipelines"
                clearable
                data={pipelines.map((p) => ({ value: p.pipeline_id, label: p.name }))}
                value={pipelineId}
                onChange={setPipelineId}
                w={260}
              />
              <Select
                label="Status"
                placeholder="Any status"
                clearable
                data={STATUS_FILTER_OPTIONS}
                value={status}
                onChange={setStatus}
                w={200}
              />
            </Group>

            {summary && (
              <SimpleGrid cols={{ base: 2, md: 5 }} spacing="sm">
                <Kpi
                  label="Runs"
                  value={formatCount(summary.runs)}
                  hint={
                    summary.runs_in_flight ? `${summary.runs_in_flight} still running` : undefined
                  }
                />
                <Kpi label="Loaded to Veeva" value={formatCount(summary.uploaded)} />
                <Kpi label="Failed documents" value={formatCount(summary.failed)} />
                <Kpi
                  label="Run success rate"
                  value={formatPercent(summary.success_rate)}
                  hint={`${summary.runs_succeeded} of ${summary.runs_succeeded + summary.runs_failed} finished runs`}
                />
                <Kpi label="Average run" value={formatDuration(summary.avg_duration_seconds)} />
              </SimpleGrid>
            )}

            {summary && <TrendChart trend={summary.trend} bucket={summary.bucket} />}

            <Paper withBorder radius="md" p="md">
              <Text fw={600} mb="xs">
                Runs
              </Text>
              {runs === null ? (
                <Loader size="sm" aria-label="Loading runs" />
              ) : runs.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No runs in this period.
                </Text>
              ) : (
                <ScrollArea type="auto">
                  <Table highlightOnHover withTableBorder fz="sm" miw={820}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Started (UTC)</Table.Th>
                        <Table.Th>Pipeline</Table.Th>
                        <Table.Th>Trigger</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th ta="right">Loaded</Table.Th>
                        <Table.Th ta="right">Failed</Table.Th>
                        <Table.Th>Duration</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {runs.map((r) => (
                        <Table.Tr
                          key={r.run_id}
                          onClick={() => setSelected(r.run_id)}
                          bg={
                            selected === r.run_id ? "var(--mantine-primary-color-light)" : undefined
                          }
                          style={{ cursor: "pointer" }}
                          aria-selected={selected === r.run_id}
                        >
                          <Table.Td>{formatUtc(r.started_at_utc)}</Table.Td>
                          <Table.Td>
                            <Group gap={6} wrap="nowrap">
                              {r.pipeline_name ?? r.pipeline_id}
                              {r.lifecycle_state && r.lifecycle_state !== "production" && (
                                <Badge
                                  size="xs"
                                  variant="outline"
                                  color={lifecycleColor(r.lifecycle_state)}
                                  tt="capitalize"
                                >
                                  {r.lifecycle_state}
                                </Badge>
                              )}
                            </Group>
                          </Table.Td>
                          <Table.Td>{TRIGGER_LABEL[r.trigger] ?? r.trigger}</Table.Td>
                          <Table.Td>
                            <Badge
                              color={runResult(r).color || statusColor(r.status)}
                              variant="light"
                            >
                              {runResult(r).label}
                            </Badge>
                          </Table.Td>
                          <Table.Td ta="right">{formatCount(r.uploaded)}</Table.Td>
                          <Table.Td ta="right">{formatCount(r.failed)}</Table.Td>
                          <Table.Td>{formatDuration(r.duration_seconds)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Paper>

            {selected && (
              // At least a screen tall, so even the last run's details can be
              // scrolled to the top of the page.
              <div ref={detailRef} style={{ scrollMarginTop: 72, minHeight: "calc(100vh - 88px)" }}>
                <RunResultDetail
                  key={selected}
                  solutionPage={solutionPage}
                  runId={selected}
                  onLoaded={scrollToDetail}
                />
              </div>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}
